import React, { useState, useEffect } from "react";
import { detectDifficulty, buildIASchedule, uploadFiles, getSubjects, addSubject, deleteSubject } from "../api";
import { C, LAYOUT } from "../constants";
import PageHeader from "../components/PageHeader";
import Card from "../components/Card";
import Btn from "../components/Btn";

const DIFF_COLORS = {
  Hard:   { bg: C.errorBg, color: C.error, border: `${C.error}55` },
  Medium: { bg: C.warningBg, color: C.warning, border: `${C.warning}55` },
  Easy:   { bg: C.successBg, color: C.success, border: `${C.success}55` },
};
const COMPAT_COLOR = s => s >= 8 ? C.success : s >= 5 ? C.warning : C.error;

export default function IAScheduler() {
  const [savedSubjects,   setSavedSubjects]   = useState([]);  // from /api/subjects (auto-loaded)
  const [extraName,       setExtraName]       = useState("");   // manual add
  const [extraSyllabus,   setExtraSyllabus]   = useState("");
  const [addingExtra,     setAddingExtra]     = useState(false);
  const [examDays,        setExamDays]        = useState(4);
  const [subjects,        setSubjects]        = useState([]);   // with difficulty scores
  const [schedule,        setSchedule]        = useState(null);
  const [loading,         setLoading]         = useState(false);
  const [fetchingInit,    setFetchingInit]    = useState(true);
  const [step,            setStep]            = useState(1);   // 1=subjects, 2=difficulty, 3=schedule
  const [msg,             setMsg]             = useState(null);
  const [uploadingId,     setUploadingId]     = useState(null);

  // Auto-load subjects saved from the main Planner upload
  useEffect(() => {
    getSubjects()
      .then(r => { if (r.success) setSavedSubjects(r.subjects || []); })
      .catch(() => {})
      .finally(() => setFetchingInit(false));
  }, []);

  const handleUploadSyllabus = async (subjectId, files) => {
    if (!files.length) return;
    setUploadingId(subjectId);
    try {
      const res = await uploadFiles({ syllabus: Array.from(files) });
      const text = (res.extracted?.syllabus || []).map(f => f.content).join("\n");
      const target = savedSubjects.find(s => s._id === subjectId);
      if (target?.name) {
        await addSubject(target.name, text).catch(() => {});
      }
      setSavedSubjects(prev => prev.map(s =>
        s._id === subjectId ? { ...s, syllabusText: text } : s
      ));
    } catch { /* ignore */ }
    setUploadingId(null);
  };

  const handleAddExtra = async () => {
    if (!extraName.trim()) return;
    setLoading(true);
    try {
      const res = await addSubject(extraName.trim(), extraSyllabus);
      if (res.success) {
        setSavedSubjects(prev => [...prev, res.subject]);
        setExtraName(""); setExtraSyllabus(""); setAddingExtra(false);
      }
    } catch (err) { setMsg({ type: "error", text: err.response?.data?.error || "Add failed" }); }
    setLoading(false);
  };

  const handleDeleteSaved = async (id, name) => {
    if (!window.confirm(`Remove "${name}" from your subject list?`)) return;
    try {
      await deleteSubject(id);
      setSavedSubjects(prev => prev.filter(s => s._id !== id));
    } catch { setMsg({ type: "error", text: "Delete failed" }); }
  };

  const handleDetect = async () => {
    const toDetect = savedSubjects.filter(s => s.name);
    if (toDetect.length < 1) { setMsg({ type: "error", text: "Add at least 1 subject" }); return; }
    setLoading(true); setMsg(null);
    try {
      const res = await detectDifficulty(toDetect);
      if (res.success) { setSubjects(res.subjects); setStep(2); }
    } catch (err) { setMsg({ type: "error", text: err.response?.data?.error || "Detection failed" }); }
    setLoading(false);
  };

  const handleBuild = async () => {
    setLoading(true); setMsg(null);
    try {
      const res = await buildIASchedule(subjects, examDays, {});
      if (res.success) { setSchedule(res.schedule); setStep(3); }
    } catch (err) { setMsg({ type: "error", text: err.response?.data?.error || "Schedule failed" }); }
    setLoading(false);
  };

  const updateDifficulty = (idx, field, value) =>
    setSubjects(p => p.map((s, i) => i === idx ? { ...s, [field]: field === "score" ? Number(value) : value } : s));

  return (
    <div style={{ maxWidth: 760, margin: "0 auto", padding: "8px 4px 28px" }}>
      <PageHeader
        title="IA Scheduler"
        subtitle="Subjects from the Planner are pre-loaded. AI detects difficulty from syllabus, then builds a balanced IA schedule."
        icon="🎯"
      />

      {/* Steps */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        {["1. Subjects", "2. Difficulty", "3. Schedule"].map((label, i) => (
          <div key={i} style={{ padding: "6px 14px", borderRadius: 99, fontSize: 12, fontWeight: 600,
            background: step === i + 1 ? C.primary : step > i + 1 ? C.successBg : C.surfaceAlt,
            color: step === i + 1 ? "#fff" : step > i + 1 ? C.success : C.muted }}>
            {step > i + 1 ? "✓ " : ""}{label}
          </div>
        ))}
      </div>

      {msg && <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 16, fontSize: 13,
        background: C.errorBg, color: C.error }}>{msg.text}</div>}

      {/* STEP 1: Subjects */}
      {step === 1 && (
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>Subjects</h3>
            <span style={{ fontSize: 12, color: C.muted }}>
              {fetchingInit ? "Loading from Planner..." : `${savedSubjects.length} subject(s) loaded`}
            </span>
          </div>

          {savedSubjects.length === 0 && !fetchingInit && (
            <div style={{ padding: "16px", background: "#E7F0F6", border: "1px solid #B6D4E8",
              borderRadius: 8, fontSize: 13, color: C.warning, marginBottom: 14 }}>
              No subjects found from your Planner uploads yet. Add them manually below, or generate a plan
              first — subjects will be auto-saved for reuse here.
            </div>
          )}

          {savedSubjects.map((s) => (
            <div key={s._id} style={{ display: "flex", gap: 8, alignItems: "center",
              padding: "8px 12px", border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 8,
              background: s.syllabusText ? "#ECFDF5" : C.bg }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: C.text }}>{s.name}</div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                  {s.source === "planner" ? "📋 From Planner" : "✏️ Manual"}
                  {s.syllabusText ? ` · ✅ Syllabus loaded (${s.syllabusText.length} chars)` : " · No syllabus yet"}
                </div>
              </div>
              <label style={{ cursor: "pointer", padding: "5px 12px", background: "#E8F0FF", color: C.primary,
                border: "1px solid #3D7EFF", borderRadius: 6, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>
                {uploadingId === s._id ? "..." : s.syllabusText ? "Re-upload" : "Upload Syllabus"}
                <input type="file" accept=".pdf,.txt,.docx" style={{ display: "none" }}
                  onChange={e => handleUploadSyllabus(s._id, e.target.files)} />
              </label>
              <button onClick={() => handleDeleteSaved(s._id, s.name)}
                style={{ background: "#FEF2F2", color: C.error, border: "1px solid #FCA5A5",
                  borderRadius: 6, padding: "5px 10px", fontSize: 12, cursor: "pointer" }}>🗑</button>
            </div>
          ))}

          {/* Add Extra Subject */}
          {addingExtra ? (
            <div style={{ border: `1.5px dashed ${C.primary}`, borderRadius: 8, padding: "12px 14px", marginTop: 8 }}>
              <input value={extraName} onChange={e => setExtraName(e.target.value)}
                placeholder="Subject name (e.g. Computer Networks)"
                style={{ width: "100%", padding: "8px 10px", border: `1.5px solid ${C.border}`,
                  borderRadius: 6, fontSize: 13, boxSizing: "border-box", marginBottom: 8 }} />
              <textarea value={extraSyllabus} onChange={e => setExtraSyllabus(e.target.value)}
                placeholder="Paste syllabus text (optional — helps AI detect difficulty accurately)"
                rows={3} style={{ width: "100%", padding: "8px 10px", border: `1.5px solid ${C.border}`,
                  borderRadius: 6, fontSize: 12, boxSizing: "border-box", resize: "vertical", marginBottom: 8 }} />
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={handleAddExtra} disabled={!extraName.trim() || loading}
                  style={{ background: C.primary, color: "#fff", border: "none", borderRadius: 6,
                    padding: "7px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                  ➕ Add Subject
                </button>
                <button onClick={() => { setAddingExtra(false); setExtraName(""); setExtraSyllabus(""); }}
                  style={{ background: C.bg, color: C.muted, border: `1px solid ${C.border}`,
                    borderRadius: 6, padding: "7px 14px", fontSize: 13, cursor: "pointer" }}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button onClick={() => setAddingExtra(true)}
              style={{ background: "transparent", color: C.primary, border: `1.5px dashed ${C.primary}`,
                borderRadius: 8, padding: "8px 16px", fontSize: 13, cursor: "pointer", width: "100%", marginTop: 8 }}>
              + Add Extra Subject
            </button>
          )}

          <div style={{ display: "flex", gap: 14, alignItems: "center", marginTop: 16 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>
                Exam Days Available
              </label>
              <input type="number" min={1} max={10} value={examDays}
                onChange={e => setExamDays(Number(e.target.value))}
                style={{ width: 80, padding: "8px 12px", border: `1.5px solid ${C.border}`,
                  borderRadius: 8, fontSize: 14 }} />
            </div>
            <button onClick={handleDetect} disabled={loading || savedSubjects.length === 0}
              style={{ marginTop: 18, background: C.primary, color: "#fff", border: "none", borderRadius: 8,
                padding: "10px 24px", fontSize: 14, fontWeight: 600,
                cursor: (loading || savedSubjects.length === 0) ? "not-allowed" : "pointer",
                opacity: (loading || savedSubjects.length === 0) ? 0.6 : 1 }}>
              {loading ? "Detecting..." : "🤖 Auto-Detect Difficulty →"}
            </button>
          </div>
        </div>
      )}

      {/* STEP 2: Difficulty Review */}
      {step === 2 && (
        <div>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>AI-Detected Difficulty</h3>
            <p style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>Adjust if needed — then build the schedule.</p>
            {subjects.map((s, i) => {
              const dc = DIFF_COLORS[s.label] || DIFF_COLORS.Medium;
              return (
                <div key={i} style={{ display: "flex", gap: 10, alignItems: "center", padding: "10px 14px",
                  border: `1px solid ${dc.border}`, background: dc.bg, borderRadius: 8, marginBottom: 8 }}>
                  <div style={{ flex: 1, fontWeight: 600, fontSize: 14, color: C.text }}>{s.name}</div>
                  <select value={s.label} onChange={e => updateDifficulty(i, "label", e.target.value)}
                    style={{ padding: "5px 10px", border: `1px solid ${dc.border}`, borderRadius: 6,
                      background: dc.bg, color: dc.color, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                    <option>Easy</option><option>Medium</option><option>Hard</option>
                  </select>
                  <input type="number" min={1} max={10} value={s.score}
                    onChange={e => updateDifficulty(i, "score", e.target.value)}
                    style={{ width: 50, padding: "5px 8px", border: `1px solid ${dc.border}`,
                      borderRadius: 6, background: dc.bg, color: dc.color, fontWeight: 700,
                      fontSize: 13, textAlign: "center" }} />
                  <div style={{ fontSize: 11, color: C.muted, maxWidth: 200 }}>{s.reason}</div>
                </div>
              );
            })}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => setStep(1)}
              style={{ background: C.bg, color: C.muted, border: `1px solid ${C.border}`,
                borderRadius: 8, padding: "10px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
              ← Back
            </button>
            <button onClick={handleBuild} disabled={loading}
              style={{ background: C.primary, color: "#fff", border: "none", borderRadius: 8,
                padding: "10px 24px", fontSize: 14, fontWeight: 600,
                cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.6 : 1 }}>
              {loading ? "Building..." : "📅 Build IA Schedule →"}
            </button>
          </div>
        </div>
      )}

      {/* STEP 3: Schedule Result */}
      {step === 3 && schedule && (
        <div>
          <div style={{ background: C.successBg, border: "1px solid #6EE7B7", borderRadius: 10,
            padding: "10px 16px", marginBottom: 16, fontSize: 13, color: C.success, fontWeight: 600 }}>
            ✅ {schedule.summary}
          </div>
          {schedule.schedule.map((day, i) => (
            <div key={i} style={{ background: C.surface, border: `1px solid ${C.border}`,
              borderRadius: 10, padding: 16, marginBottom: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 16, color: C.primary, marginBottom: 10 }}>
                {day.day}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {["FN", "AN"].map(slot => {
                  const subj = day[slot];
                  if (!subj) return (
                    <div key={slot} style={{ padding: 12, background: C.bg, borderRadius: 8,
                      color: C.muted, fontSize: 13, textAlign: "center" }}>
                      — {slot} Empty
                    </div>
                  );
                  const dc = DIFF_COLORS[subj.difficulty] || DIFF_COLORS.Medium;
                  return (
                    <div key={slot} style={{ padding: 12, background: dc.bg,
                      border: `1px solid ${dc.border}`, borderRadius: 8 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: dc.color, marginBottom: 4 }}>
                        {slot} SESSION
                      </div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 4 }}>
                        {subj.name}
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 99,
                        background: dc.bg, color: dc.color, border: `1px solid ${dc.border}` }}>
                        {subj.difficulty} (Score: {subj.score})
                      </span>
                    </div>
                  );
                })}
              </div>
              {day.compatibility !== null && (
                <div style={{ marginTop: 8, fontSize: 12, fontWeight: 600, color: COMPAT_COLOR(day.compatibility) }}>
                  Compatibility: {day.compatibility}/10 {day.compatibility >= 8 ? "✅ Good" : day.compatibility >= 5 ? "⚠️ Acceptable" : "❌ Not ideal"}
                </div>
              )}
              {day.warning && <div style={{ marginTop: 6, fontSize: 12, color: C.error }}>{day.warning}</div>}
            </div>
          ))}
          <button onClick={() => setStep(1)}
            style={{ background: C.bg, color: C.muted, border: `1px solid ${C.border}`,
              borderRadius: 8, padding: "10px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
            ← Start Over
          </button>
        </div>
      )}
    </div>
  );
}
